"use client";

import { Eye } from "lucide-react";
import { BlogStatusBadge } from "@/components/features/blog/BlogStatusBadge";
import { Link } from "@/i18n/navigation";

type StatementCardStatus =
  | "draft"
  | "under_voting"
  | "approved"
  | "rejected"
  | "executed";

interface StatementItem {
  id: string;
  index: number;
  slug: string;
  title: string;
  author_wallet: string;
  status: StatementCardStatus;
  published_at: string | null;
  voting_ends_at: string | null;
  yes_weight: string;
  no_weight: string;
  net_weight: string;
  support_ratio: number;
  view_count: number;
}

interface MyStatementCardProps {
  statement: StatementItem;
}

// Net-support % colour mirrors the status pill colour (yellow approved, blue
// under_voting, green executed, red rejected). Drafts show no percentage.
const PCT_COLOR: Record<StatementCardStatus, string> = {
  draft: "",
  under_voting: "text-semantic-link",
  approved: "text-accent",
  rejected: "text-semantic-error",
  executed: "text-semantic-success",
};

export const MyStatementCard: React.FC<MyStatementCardProps> = ({
  statement,
}) => {
  const supportPercent = Math.round((statement.support_ratio ?? 0) * 100);
  const isDraft = statement.status === "draft";
  // Drafts aren't public — the eye opens the editor; published rows open /blog.
  const href = isDraft
    ? `/info/statement?draft=${statement.id}`
    : `/blog/${statement.slug}`;

  return (
    <div className="flex items-center gap-3 rounded-md bg-bg-secondary px-4 py-3">
      <h3 className="flex-1 truncate text-sm font-medium text-text-primary">
        {statement.title}
      </h3>

      {!isDraft && (
        <bdi
          dir="ltr"
          className={`shrink-0 text-sm font-bold ${PCT_COLOR[statement.status]}`}
        >
          {supportPercent}% DNM
        </bdi>
      )}

      <BlogStatusBadge status={statement.status} className="shrink-0" />

      <Link
        href={href}
        aria-label={statement.title}
        className="grid size-10 shrink-0 place-items-center rounded-lg border border-stroke-light text-text-secondary transition-colors hover:bg-bg-tertiary"
      >
        <Eye size={18} aria-hidden="true" />
      </Link>
    </div>
  );
};
